import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { generateProductionReport, getProductionStats } from "../../services/supervisorService";
import { format, subDays } from 'date-fns';
import {
    AiOutlineCalendar,
    AiOutlineFilePdf,
    AiOutlineFileExcel,
    AiOutlineBarChart,
    AiOutlineLineChart,
    AiOutlineDownload,
    AiOutlinePrinter,
    AiOutlineReload
} from "react-icons/ai";

const ReportGenerator = ({ showPopup }) => {
    const [dateRange, setDateRange] = useState({
        start_date: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
        end_date: format(new Date(), 'yyyy-MM-dd')
    });

    const [reportFormat, setReportFormat] = useState("pdf");
    const [reportType, setReportType] = useState("production");
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [previewReady, setPreviewReady] = useState(false);

    useEffect(() => {
        if (reportData) {
            generateReportPreview();
        }
    }, [reportData]);

    const handleDateChange = (e) => {
        const { name, value } = e.target;
        setDateRange(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const fetchReportData = async () => {
        setLoading(true);
        setPreviewReady(false);

        try {
            const response = await getProductionStats(dateRange);

            if (response && response.success) {
                setReportData(response.data);
            } else {
                showPopup("Failed to fetch report data", "error");
                setReportData(null);
            }
        } catch (error) {
            console.error("Error fetching report data:", error);
            showPopup("Failed to fetch report data", "error");
            setReportData(null);
        } finally {
            setLoading(false);
        }
    };

    const generateReportPreview = () => {
        // In a real app, this would generate a preview
        // For now, we'll simulate a delay
        setTimeout(() => {
            setPreviewReady(true);
        }, 500);
    };

    const downloadReport = async () => {
        setLoading(true);

        try {
            const response = await generateProductionReport({
                ...dateRange,
                format: reportFormat,
                report_type: reportType
            });

            if (response && response.success) {
                showPopup(`Report downloaded successfully`, "success");
            } else {
                showPopup("Failed to generate report", "error");
            }
        } catch (error) {
            console.error("Error generating report:", error);
            showPopup("Failed to generate report", "error");
        } finally {
            setLoading(false);
        }
    };

    const printReport = () => {
        window.print();
    };

    const formatDate = (dateString) => {
        try {
            return format(new Date(dateString), 'MMMM dd, yyyy');
        } catch {
            return dateString;
        }
    };

    const getReportTypeName = () => {
        switch (reportType) {
            case "production":
                return "Production Summary";
            case "efficiency":
                return "Efficiency Analysis";
            case "issues":
                return "Issues Report";
            default:
                return "Production Summary";
        }
    };

    return (
        <Container>
            <Header>
                <h2>Report Generator</h2>
                <RefreshButton onClick={fetchReportData}>
                    <AiOutlineReload />
                </RefreshButton>
            </Header>

            <ControlsSection>
                <ControlGroup>
                    <ControlLabel>
                        <AiOutlineCalendar />
                        <span>Date Range:</span>
                    </ControlLabel>
                    <DateInputsRow>
                        <div>
                            <SmallLabel>From:</SmallLabel>
                            <DateInput
                                type="date"
                                name="start_date"
                                value={dateRange.start_date}
                                onChange={handleDateChange}
                            />
                        </div>
                        <div>
                            <SmallLabel>To:</SmallLabel>
                            <DateInput
                                type="date"
                                name="end_date"
                                value={dateRange.end_date}
                                onChange={handleDateChange}
                            />
                        </div>
                    </DateInputsRow>
                </ControlGroup>

                <ControlsRow>
                    <ControlGroup>
                        <ControlLabel>
                            <AiOutlineBarChart />
                            <span>Report Type:</span>
                        </ControlLabel>
                        <SelectInput
                            value={reportType}
                            onChange={(e) => setReportType(e.target.value)}
                        >
                            <option value="production">Production Summary</option>
                            <option value="efficiency">Efficiency Analysis</option>
                            <option value="issues">Issues Report</option>
                        </SelectInput>
                    </ControlGroup>

                    <ControlGroup>
                        <ControlLabel>
                            <AiOutlineFilePdf />
                            <span>Format:</span>
                        </ControlLabel>
                        <RadioGroup>
                            <RadioOption>
                                <input
                                    type="radio"
                                    id="pdf"
                                    name="format"
                                    value="pdf"
                                    checked={reportFormat === "pdf"}
                                    onChange={() => setReportFormat("pdf")}
                                />
                                <label htmlFor="pdf">PDF</label>
                            </RadioOption>
                            <RadioOption>
                                <input
                                    type="radio"
                                    id="excel"
                                    name="format"
                                    value="excel"
                                    checked={reportFormat === "excel"}
                                    onChange={() => setReportFormat("excel")}
                                />
                                <label htmlFor="excel">Excel</label>
                            </RadioOption>
                        </RadioGroup>
                    </ControlGroup>
                </ControlsRow>

                <ActionButtonsRow>
                    <GenerateButton onClick={fetchReportData} disabled={loading}>
                        {loading ? "Generating..." : "Generate Preview"}
                    </GenerateButton>

                    <ActionButtons>
                        <ActionButton
                            onClick={downloadReport}
                            disabled={!reportData || loading}
                            className="download"
                        >
                            <AiOutlineDownload />
                            <span>Download</span>
                        </ActionButton>

                        <ActionButton
                            onClick={printReport}
                            disabled={!previewReady || loading}
                            className="print"
                        >
                            <AiOutlinePrinter />
                            <span>Print</span>
                        </ActionButton>
                    </ActionButtons>
                </ActionButtonsRow>
            </ControlsSection>

            <PreviewSection className="print-section">
                <PreviewHeader>
                    <h3>Report Preview</h3>
                </PreviewHeader>

                {loading ? (
                    <LoadingState>Generating report preview...</LoadingState>
                ) : !reportData ? (
                    <EmptyState>
                        <p>No report data yet</p>
                        <small>Select your report parameters and click Generate Preview</small>
                    </EmptyState>
                ) : !previewReady ? (
                    <LoadingState>Preparing preview...</LoadingState>
                ) : (
                    <ReportPreview>
                        <ReportTitle>
                            <h2>W. THOMAS & SONS</h2>
                            <h3>{getReportTypeName()}</h3>
                            <p>Period: {formatDate(dateRange.start_date)} to {formatDate(dateRange.end_date)}</p>
                        </ReportTitle>

                        <ReportSection>
                            <SectionTitle>Production Summary</SectionTitle>
                            <SummaryStats>
                                <StatCard>
                                    <StatValue>{reportData.summary.total_production || 0}</StatValue>
                                    <StatLabel>Total Production</StatLabel>
                                </StatCard>
                                <StatCard>
                                    <StatValue>{reportData.summary.total_activities || 0}</StatValue>
                                    <StatLabel>Activities</StatLabel>
                                </StatCard>
                                <StatCard>
                                    <StatValue>{reportData.summary.total_workdays || 0}</StatValue>
                                    <StatLabel>Work Days</StatLabel>
                                </StatCard>
                                <StatCard>
                                    <StatValue>{reportData.summary.total_issues || 0}</StatValue>
                                    <StatLabel>Issues</StatLabel>
                                </StatCard>
                            </SummaryStats>
                        </ReportSection>

                        {reportData.production_by_product && reportData.production_by_product.length > 0 && (
                            <ReportSection>
                                <SectionTitle>Production by Product</SectionTitle>
                                <DataTable>
                                    <thead>
                                    <tr>
                                        <th>Product</th>
                                        <th>Quantity</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {reportData.production_by_product.map((item) => (
                                        <tr key={item.id}>
                                            <td>{item.name}</td>
                                            <td>{item.total_quantity}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </DataTable>
                            </ReportSection>
                        )}

                        {reportData.daily_production && reportData.daily_production.length > 0 && (
                            <ReportSection>
                                <SectionTitle>Daily Production</SectionTitle>
                                <DataTable>
                                    <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Production</th>
                                        <th>Activities</th>
                                        <th>Issues</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {reportData.daily_production.map((day, index) => (
                                        <tr key={index}>
                                            <td>{formatDate(day.date)}</td>
                                            <td>{day.total_production}</td>
                                            <td>{day.activities_count}</td>
                                            <td>{day.issues_count}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </DataTable>
                            </ReportSection>
                        )}

                        <ReportFooter>
                            <p>Generated on: {format(new Date(), 'MMMM dd, yyyy')}</p>
                            <p>This report is computer-generated and does not require a signature.</p>
                        </ReportFooter>
                    </ReportPreview>
                )}
            </PreviewSection>
        </Container>
    );
};

export default ReportGenerator;

// Styled Components
const Container = styled.div`
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    padding: 20px;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    
    h2 {
        font-size: 18px;
        margin: 0;
        color: #333;
    }
`;

const RefreshButton = styled.button`
    background: none;
    border: none;
    color: #777;
    cursor: pointer;
    font-size: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
        color: #000;
    }
`;

const ControlsSection = styled.div`
    background-color: #f9f9f9;
    border-radius: 8px;
    padding: 15px;
    margin-bottom: 20px;
`;

const ControlsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    margin-bottom: 15px;
    
    @media (max-width: 768px) {
        flex-direction: column;
        gap: 15px;
    }
`;

const ControlGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    min-width: 200px;
`;

const ControlLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    font-weight: 500;
    color: #555;
    
    svg {
        font-size: 16px;
    }
`;

const SmallLabel = styled.label`
    font-size: 12px;
    color: #666;
    margin-bottom: 4px;
    display: block;
`;

const DateInputsRow = styled.div`
    display: flex;
    gap: 10px;
    
    > div {
        flex: 1;
    }
`;

const DateInput = styled.input`
    width: 100%;
    padding: 8px 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    
    &:focus {
        outline: none;
        border-color: #2196F3;
    }
`;

const SelectInput = styled.select`
    width: 100%;
    padding: 8px 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    background-color: white;
    
    &:focus {
        outline: none;
        border-color: #2196F3;
    }
`;

const RadioGroup = styled.div`
    display: flex;
    gap: 15px;
`;

const RadioOption = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    
    label {
        font-size: 14px;
        cursor: pointer;
    }
    
    input {
        cursor: pointer;
    }
`;

const ActionButtonsRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 20px;
    flex-wrap: wrap;
    gap: 15px;
    
    @media (max-width: 576px) {
        flex-direction: column;
        align-items: stretch;
    }
`;

const GenerateButton = styled.button`
    background-color: #000;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 10px 16px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 150px;
    
    &:hover:not(:disabled) {
        background-color: #333;
    }
    
    &:disabled {
        background-color: #ccc;
        cursor: not-allowed;
    }
`;

const ActionButtons = styled.div`
    display: flex;
    gap: 10px;
`;

const ActionButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    background-color: #f5f5f5;
    color: #555;
    border: none;
    border-radius: 4px;
    padding: 10px 16px;
    font-size: 14px;
    cursor: pointer;
    
    &:hover:not(:disabled) {
        background-color: #e0e0e0;
    }
    
    &:disabled {
        color: #aaa;
        cursor: not-allowed;
    }
    
    svg {
        font-size: 16px;
    }
    
    &.download {
        background-color: #e3f2fd;
        color: #1976d2;
        
        &:hover:not(:disabled) {
            background-color: #bbdefb;
        }
    }
    
    &.print {
        background-color: #e8f5e9;
        color: #388e3c;
        
        &:hover:not(:disabled) {
            background-color: #c8e6c9;
        }
    }
`;

const PreviewSection = styled.div`
    border: 1px solid #eee;
    border-radius: 8px;
`;

const PreviewHeader = styled.div`
    background-color: #f5f5f5;
    padding: 12px 15px;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    border-bottom: 1px solid #eee;
    
    h3 {
        font-size: 16px;
        margin: 0;
        color: #333;
    }
`;

const LoadingState = styled.div`
    text-align: center;
    padding: 50px 20px;
    color: #777;
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 50px 20px;
    color: #777;
    
    p {
        font-size: 16px;
        margin-bottom: 5px;
    }
    
    small {
        font-size: 14px;
        color: #999;
    }
`;

const ReportPreview = styled.div`
    padding: 30px;
    
    @media print {
        padding: 0;
    }
`;

const ReportTitle = styled.div`
    text-align: center;
    margin-bottom: 30px;
    
    h2 {
        font-size: 20px;
        margin: 0 0 5px 0;
    }
    
    h3 {
        font-size: 18px;
        margin: 0 0 10px 0;
        font-weight: 500;
    }
    
    p {
        font-size: 14px;
        color: #666;
        margin: 0;
    }
`;

const ReportSection = styled.div`
    margin-bottom: 30px;
`;

const SectionTitle = styled.h4`
    font-size: 16px;
    color: #333;
    margin: 0 0 15px 0;
    padding-bottom: 8px;
    border-bottom: 1px solid #eee;
`;

const SummaryStats = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 15px;
    margin-bottom: 20px;
`;

const StatCard = styled.div`
    background-color: #f9f9f9;
    border-radius: 6px;
    padding: 15px;
    text-align: center;
`;

const StatValue = styled.div`
    font-size: 24px;
    font-weight: 600;
    color: #333;
    margin-bottom: 5px;
`;

const StatLabel = styled.div`
    font-size: 13px;
    color: #666;
`;

const DataTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    
    th, td {
        padding: 10px 12px;
        text-align: left;
        border: 1px solid #eee;
    }
    
    th {
        background-color: #f5f5f5;
        font-weight: 600;
        font-size: 13px;
    }
    
    td {
        font-size: 13px;
    }
    
    tr:nth-child(even) {
        background-color: #fafafa;
    }
`;

const ReportFooter = styled.div`
    margin-top: 40px;
    text-align: center;
    font-size: 13px;
    color: #777;
    
    p {
        margin: 5px 0;
    }
`;